import { enUS } from "@mui/material/locale";
import { enUS as enUS_charts } from "@mui/x-charts/locales";
import { enUS as enUS_dataGrid } from "@mui/x-data-grid/locales";
import { createTheme } from "@mui/material/styles";
import { grey } from "@mui/material/colors";
// fonts
import OpenSans_600_Woff2 from "./fonts/OpenSans-600.woff2";
import OpenSans_600_Woff from "./fonts/OpenSans-600.woff";
import OpenSans_600_Italic_Woff2 from "./fonts/OpenSans-600-italic.woff2";
import OpenSans_600_Italic_Woff from "./fonts/OpenSans-600-italic.woff";
import OpenSans_700_Woff2 from "./fonts/OpenSans-700.woff2";
import OpenSans_700_Woff from "./fonts/OpenSans-700.woff";
import OpenSans_700_Italic_Woff2 from "./fonts/OpenSans-700-italic.woff2";
import OpenSans_700_Italic_Woff from "./fonts/OpenSans-700-italic.woff";
// services
import { UI_LANGUAGE } from "../../scripts/services/l10n";
// types
import type { CssVarsThemeOptions } from "@mui/material/styles";

const l10nMuiTheme = {
	en: enUS,
} as const;

const l10nCharts = {
	en: enUS_charts,
} as const;

const l10nDataGrid = {
	en: enUS_dataGrid,
} as const;

export const getTheme = (primaryColor: string) => (
	createTheme({
		cssVariables: {
			colorSchemeSelector: "class",
		},
		colorSchemes: {
			light: {
				palette: {
					primary: {
						main: primaryColor,
					},
					text: {
						primary: "#191919",
						secondary: "#828282",
					},
					background: {
						default: grey[100],
					},
					action: {
						disabledBackground: "#dcdcdc",
					},
					neutral: {
						main: grey[300],
						contrastText: grey[900],
					},
					danger: {
						main: "#ee0000",
						dark: "#bb0000",
					},
				},
			},
			dark: {
				palette: {
					primary: {
						main: primaryColor,
					},
					text: {
						primary: "#e0e0e0",
						secondary: "#949494",
					},
					background: {
						default: "#000000",
						paper: grey[900],
					},
					action: {
						disabledBackground: "rgba(255, 255, 255, 0.12)",
					},
					neutral: {
						main: grey[800],
						contrastText: grey[300],
					},
					danger: {
						main: "#ee0000",
						dark: "#bb0000",
					},
				},
			},
		},
		typography: {
			fontFamily: "OpenSans",
		},
		components: {
			MuiCssBaseline: {
				styleOverrides: `
					@font-face {
						font-family: "OpenSans";
						font-style: normal;
						font-weight: 400 600;
						src: url(${OpenSans_600_Woff2 as string}) format("woff2"), url(${OpenSans_600_Woff as string}) format("woff");
					},
					@font-face {
						font-family: "OpenSans";
						font-style: italic;
						font-weight: 400 600;
						src: url(${OpenSans_600_Italic_Woff2 as string}) format("woff2"), url(${OpenSans_600_Italic_Woff as string}) format("woff");
					},
					@font-face {
						font-family: "OpenSans";
						font-style: normal;
						font-weight: 700;
						src: url(${OpenSans_700_Woff2 as string}) format("woff2"), url(${OpenSans_700_Woff as string}) format("woff");
					},
					@font-face {
						font-family: "OpenSans";
						font-style: italic;
						font-weight: 700;
						src: url(${OpenSans_700_Italic_Woff2 as string}) format("woff2"), url(${OpenSans_700_Italic_Woff as string}) format("woff");
					}
				`,
			},
			MuiFormControl: {
				defaultProps: {
					variant: "standard",
				},
			},
			MuiIconButton: {
				defaultProps: {
					size: "large",
				},
			},
			MuiListItemIcon: {
				styleOverrides: {
					root: {
						color: "inherit",
					},
				},
			},
			MuiPaper: {
				styleOverrides: {
					root: {
						backgroundImage: "unset",
					},
				},
			},
			MuiSelect: {
				defaultProps: {
					variant: "standard",
				},
			},
			MuiSkeleton: {
				defaultProps: {
					animation: "wave",
				},
			},
			MuiSnackbar: {
				defaultProps: {
					anchorOrigin: {
						vertical: "bottom",
						horizontal: "center",
					},
				},
			},
			MuiTabs: {
				defaultProps: {
					variant: "fullWidth",
				},
				styleOverrides: {
					indicator: {
						display: "none",
					},
				},
			},
			MuiTab: {
				styleOverrides: {
					root: ({ theme }) => ({
						"color": theme.vars.palette.primary.main,
						"&.Mui-selected": {
							color: theme.vars.palette.primary.contrastText,
							backgroundColor: theme.vars.palette.primary.main,
						},
					}),
				},
			},
			MuiTabPanel: {
				styleOverrides: {
					root: {
						padding: 0,
					},
				},
			},
			MuiTableCell: {
				styleOverrides: {
					head: {
						fontWeight: 600,
					},
				},
			},
			MuiTextField: {
				defaultProps: {
					variant: "standard",
				},
			},
			MuiDataGrid: {
				defaultProps: {
					checkboxSelection: true,
					disableRowSelectionOnClick: true,
					autoHeight: true,
				},
				styleOverrides: {
					root: {
						border: "none",
						backgroundColor: "inherit",
					},
				},
			},
		},
	}, l10nMuiTheme[UI_LANGUAGE], l10nCharts[UI_LANGUAGE], l10nDataGrid[UI_LANGUAGE]) as CssVarsThemeOptions
);
